import dagre from 'dagre';
import { useMemo } from 'react';
import type { Edge, Node } from 'reactflow';
import type { NodeData } from './chart';

interface BasePath { asn: string; name: string; }
type FlowElement<T> = Node<T> | Edge<T>;
const NODE_WIDTH = 240;
const NODE_HEIGHT = 100;

export function useElements(base: BasePath, data: StructuredResponse): FlowElement<NodeData>[] {
  return useMemo(() => buildElements(base, data), [base.asn, base.name, data]);
}

function buildElements(base: BasePath, data: StructuredResponse): FlowElement<NodeData>[] {
  // Collapse adjacent prepends and deduplicate complete paths.
  const unique = new Map<string, number[]>();
  for (const route of data.routes) {
    const path = route.as_path.filter((asn, i, arr) => i === 0 || asn !== arr[i - 1]);
    if (path.length) unique.set(path.join('-'), path);
  }
  const paths = [...unique.values()];
  const graph = new dagre.graphlib.Graph();
  graph.setGraph({ rankdir: 'BT', nodesep: 48, ranksep: 90, marginx: 24, marginy: 24 });
  graph.setDefaultEdgeLabel(() => ({}));
  const nodes: Node<NodeData>[] = [];
  const edges: Edge<NodeData>[] = [];
  const addNode = (id: string, data: NodeData) => {
    graph.setNode(id, { width: NODE_WIDTH, height: NODE_HEIGHT });
    nodes.push({ id, type: 'ASNode', position: { x: 0, y: 0 }, data });
  };
  addNode(base.asn, { asn: base.asn, name: base.name, hasChildren: paths.length > 0 });
  paths.forEach((path, group) => {
    let previous = base.asn;
    path.forEach((asn, hop) => {
      const id = `path-${group}-hop-${hop}`;
      addNode(id, { asn: String(asn), name: `AS${asn}`, hasParents: true,
        hasChildren: hop < path.length - 1 });
      graph.setEdge(previous, id);
      edges.push({ id: `edge-${previous}-${id}`, source: previous, target: id });
      previous = id;
    });
  });
  dagre.layout(graph);
  for (const node of nodes) {
    const position = graph.node(node.id);
    node.position = { x: position.x - NODE_WIDTH / 2, y: position.y - NODE_HEIGHT / 2 };
  }
  return [...nodes, ...edges];
}
